import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useUISettings } from '@/hooks/useUISettings';
import { Image, Upload, Eye } from 'lucide-react';
import { CandidateLayout } from '@/components/candidate/CandidateLayout';
import { usePageMeta } from '@/hooks/usePageMeta';

const UISettings = () => {
  usePageMeta({
    title: "Configurações UI - Data Hire Club",
    description: "Configurações de interface do usuário",
    noindex: true,
  });

  const { 
    uiSettings, 
    uploadAndUpdateImage, 
    isProcessing, 
    getSettingByKey 
  } = useUISettings();
  
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string>('');

  const emptyJobsSetting = getSettingByKey('empty_jobs_image');

  useEffect(() => {
    if (emptyJobsSetting?.image_url) {
      setPreviewUrl(emptyJobsSetting.image_url);
    }
  }, [emptyJobsSetting]);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        alert('Por favor, selecione apenas arquivos de imagem.');
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        alert('A imagem deve ter no máximo 5MB.');
        return;
      }

      setSelectedFile(file);
      
      const reader = new FileReader();
      reader.onload = (e) => {
        setPreviewUrl(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadAndSave = async () => {
    if (!selectedFile) return;

    try {
      await uploadAndUpdateImage(selectedFile, 'empty_jobs_image');
      setSelectedFile(null);
    } catch (error) {
      console.error('Erro ao fazer upload e salvar:', error);
    }
  };

  return (
    <CandidateLayout maxWidth="2xl">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Image className="w-5 h-5" />
            Configurações de Interface
          </CardTitle>
          <p className="text-sm text-muted-foreground">
            Gerencie as imagens e elementos visuais da plataforma
          </p>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="space-y-4">
            <div>
              <Label className="text-sm font-medium">
                Imagem do Estado Vazio (Nenhuma vaga encontrada)
              </Label>
              <p className="text-xs text-muted-foreground mb-3">
                Esta imagem será exibida quando não houver vagas disponíveis
              </p>
            </div>

            {previewUrl && (
              <div className="space-y-2">
                <Label className="text-sm text-muted-foreground flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  Preview
                </Label>
                <div className="border-2 border-dashed border-border rounded-lg p-4">
                  <img 
                    src={previewUrl} 
                    alt="Preview da imagem de logo ou banner da página inicial" 
                    className="w-full max-w-xs mx-auto rounded-lg shadow-sm"
                    loading="lazy"
                    width="400"
                    height="300"
                  />
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="image-upload" className="text-sm font-medium">
                Selecionar nova imagem
              </Label>
              <div className="flex items-center gap-2">
                <input
                  id="image-upload"
                  type="file"
                  accept="image/*"
                  onChange={handleFileSelect}
                  className="hidden"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('image-upload')?.click()}
                  className="flex items-center gap-2"
                  disabled={isProcessing}
                >
                  <Upload className="w-4 h-4" />
                  Escolher arquivo
                </Button>
                {selectedFile && (
                  <span className="text-sm text-muted-foreground">
                    {selectedFile.name}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground">
                Formatos aceitos: JPG, PNG, GIF. Tamanho máximo: 5MB
              </p>
            </div>

            <Button 
              onClick={handleUploadAndSave}
              disabled={!selectedFile || isProcessing}
              className="w-full"
            >
              {isProcessing ? 'Processando...' : 'Salvar imagem'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </CandidateLayout>
  );
};

export default UISettings;
